import { useCallback, useMemo, type RefObject } from "react";
import {
  useSnapshot as useSnapshotBase,
  type UseSnapshotResult,
} from "@/lib/use-snapshot";
import type { PersistedBoard } from "./use-handshake-strokes";

/**
 * HandshakeBoard snapshot wrapper. Composes the shared snapshot hook
 * (`src/lib/use-snapshot.ts`) with two HAH-specific bindings:
 *
 *   - **Capture**: the visible canvas is the source of truth for
 *     pen strokes; sticky notes live in a DOM overlay layered above
 *     it. We merge both onto a hidden offscreen canvas before calling
 *     `toBlob` so the PNG carries everything the facilitator sees,
 *     not just the freehand layer. The merge mirrors the live sticky
 *     style (rgba bg + colored border + text) so the snapshot looks
 *     visually identical to the board.
 *   - **Restore**: snapshots round-trip via the persisted board JSON
 *     stored alongside the PNG. Strokes + stickies + actionLog
 *     replace the live state via `replaceAll`, which also persists
 *     the new state to idb-keyval in the same write pass.
 *
 * Legacy reference: vanilla `hbSave()` exported a PNG of the canvas
 * only (`_legacy/xaes-nav.js` near the captures handler). We extend
 * that to include the sticky layer so the snapshot is faithful.
 */

type Sticky = PersistedBoard["stickies"][number];

type Options = {
  workshopId: string;
  /** Live ref to the visible HAH canvas. */
  canvasRef: RefObject<HTMLCanvasElement | null>;
  /** Live ref to the board wrapper (used for accurate sticky positioning). */
  wrapperRef: RefObject<HTMLDivElement | null>;
  /** Current sticky list — captured into the offscreen render. */
  stickies: Sticky[];
  /** Current state, captured into `stateJson` for round-trip restore. */
  boardState: PersistedBoard;
  /** Apply a restored snapshot to the live board. */
  applyRestoredBoard: (next: PersistedBoard) => void;
};

/** Default sticky width — kept in sync with `HandshakeBoard.tsx`. */
const STICKY_WIDTH = 140;

/**
 * Resolve a `var(--…)` colour to a concrete RGB string by routing it
 * through a hidden helper element. Mirrors the same pattern used in
 * `useCanvasPainter` (HandshakeBoard.tsx) but inlined here so the
 * snapshot path doesn't reach into the painter's private cache.
 */
function resolveCssColor(input: string): string {
  if (!input.startsWith("var(") && !input.startsWith("rgba(var(")) return input;
  const helper = document.createElement("span");
  helper.style.display = "none";
  document.body.appendChild(helper);
  helper.style.color = input;
  const computed = getComputedStyle(helper).color;
  helper.remove();
  return computed || input;
}

/**
 * Render the visible canvas + the sticky layer onto an offscreen
 * canvas, then encode as a PNG `Blob`. Returns `null` if the live
 * canvas hasn't mounted yet or the encode rejects.
 */
async function captureHandshakeBlob(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  wrapperRef: RefObject<HTMLDivElement | null>,
  stickies: Sticky[],
): Promise<Blob | null> {
  const canvas = canvasRef.current;
  const wrapper = wrapperRef.current;
  if (!canvas || !wrapper) return null;

  // Off-DPR pixel dimensions — match the wrapper's CSS box so the
  // sticky positions (which are stored in CSS pixels) land correctly.
  const wrapperRect = wrapper.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.round(wrapperRect.width * dpr));
  const height = Math.max(1, Math.round(wrapperRect.height * dpr));

  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const ctx = out.getContext("2d");
  if (!ctx) return null;

  // Paint the panel background first so the PNG isn't transparent.
  const bg = resolveCssColor("var(--bg-panel)") || "#ffffff";
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  // Draw the live canvas (already DPR-scaled) verbatim. `drawImage`
  // copies the raster — no need to scale further.
  ctx.drawImage(canvas, 0, 0, width, height);

  // Composite stickies on top. We render in CSS-pixel space then
  // scale by DPR for the offscreen canvas resolution.
  ctx.save();
  ctx.scale(dpr, dpr);
  ctx.font =
    "600 11px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
  ctx.textBaseline = "top";
  for (const s of stickies) {
    const w = s.width ?? STICKY_WIDTH;
    // `rgb` may be a "r,g,b" triple or a `var(--…-rgb)` reference;
    // resolve through the helper so we always end up with concrete
    // channel values.
    let rgbTriple = s.rgb;
    if (rgbTriple.startsWith("var(")) {
      // The helper trick doesn't work on raw triples; build a probe
      // colour via `rgb(var(--…-rgb))` and parse the result.
      rgbTriple = resolveCssColor(`rgb(${s.rgb})`);
    } else {
      rgbTriple = `rgb(${rgbTriple})`;
    }
    const borderColor = resolveCssColor(s.color);

    // Background wash + border — matches the live sticky chrome
    // (rgba 0.18 fill, rgba 0.4 border) inside HandshakeBoard.tsx.
    const channelMatch = rgbTriple.match(/\d+/g);
    const r = channelMatch?.[0] ?? "0";
    const g = channelMatch?.[1] ?? "0";
    const b = channelMatch?.[2] ?? "0";
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.18)`;
    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.4)`;
    ctx.lineWidth = 1;

    // Estimate sticky height from text length — keeps the snapshot
    // visually close to the live render without forcing us to spin
    // up a full text-layout engine.
    const estimatedLines = Math.max(1, Math.ceil(s.text.length / 22));
    const h = 16 + estimatedLines * 14 + 4;

    const rx = 6;
    // Rounded rect — manual path since `ctx.roundRect` isn't yet
    // universally available.
    ctx.beginPath();
    ctx.moveTo(s.x + rx, s.y);
    ctx.lineTo(s.x + w - rx, s.y);
    ctx.quadraticCurveTo(s.x + w, s.y, s.x + w, s.y + rx);
    ctx.lineTo(s.x + w, s.y + h - rx);
    ctx.quadraticCurveTo(s.x + w, s.y + h, s.x + w - rx, s.y + h);
    ctx.lineTo(s.x + rx, s.y + h);
    ctx.quadraticCurveTo(s.x, s.y + h, s.x, s.y + h - rx);
    ctx.lineTo(s.x, s.y + rx);
    ctx.quadraticCurveTo(s.x, s.y, s.x + rx, s.y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Word-wrap the sticky text. Cheap greedy wrap — character count
    // proxy is good enough for the snapshot use case.
    ctx.fillStyle = borderColor;
    const words = s.text.split(/\s+/);
    let line = "";
    let y = s.y + 8;
    const maxChars = 22;
    for (const word of words) {
      const tentative = line ? `${line} ${word}` : word;
      if (tentative.length > maxChars && line) {
        ctx.fillText(line, s.x + 10, y);
        y += 14;
        line = word;
      } else {
        line = tentative;
      }
    }
    if (line) ctx.fillText(line, s.x + 10, y);
  }
  ctx.restore();

  return await new Promise<Blob | null>((resolve) => {
    out.toBlob(
      (blob) => resolve(blob),
      "image/png",
    );
  });
}

export function useHandshakeSnapshot(opts: Options): UseSnapshotResult {
  const {
    workshopId,
    canvasRef,
    wrapperRef,
    stickies,
    boardState,
    applyRestoredBoard,
  } = opts;

  // The capture closure must read live refs each time — we wrap in a
  // useCallback that only depends on the stickies/refs identities so
  // the shared hook isn't paying for fresh closures on every paint.
  const captureBlob = useCallback(
    () => captureHandshakeBlob(canvasRef, wrapperRef, stickies),
    [canvasRef, wrapperRef, stickies],
  );

  // `stateJson` carries the persisted board envelope. We re-serialize
  // on every render so the closure captures the latest reference;
  // the cost is trivial (single JSON.stringify) and avoids state-tear
  // between the capture and the structured snapshot.
  const captureStateJson = useCallback(() => {
    try {
      return JSON.stringify(boardState);
    } catch {
      return undefined;
    }
  }, [boardState]);

  const restoreFromSnapshot = useCallback(
    async (meta: { stateJson?: string }) => {
      if (!meta.stateJson) return;
      try {
        const parsed = JSON.parse(meta.stateJson) as PersistedBoard;
        applyRestoredBoard(parsed);
      } catch {
        // Malformed snapshot — leave the board untouched.
      }
    },
    [applyRestoredBoard],
  );

  return useSnapshotBase(
    useMemo(
      () => ({
        workshopId,
        kind: "handshake" as const,
        captureBlob,
        captureStateJson,
        restoreFromSnapshot,
      }),
      [workshopId, captureBlob, captureStateJson, restoreFromSnapshot],
    ),
  );
}
